import { Recording } from './types';
import { StorageService } from './StorageService';

const PROCESSING_MESSAGE =
  'This recording is still processing. Please check back in a few moments once it is ready.';

/**
 * Service for downloading recordings.
 *
 * Mixing and muxing happen server-side (the Jamrock pipeline). The client only ever
 * downloads the finished media: `mixed_recording_url` for audio and `final_video_url`
 * for video. If the finished media is not ready yet, a clear "processing" error is
 * thrown for the UI to surface.
 */
export class DownloadService {
  private storageService = new StorageService();

  async createVideoDownloadModal(recording: Recording): Promise<void> {
    return this.downloadFinishedMedia(recording);
  }

  async downloadMixedAudio(recording: Recording): Promise<void> {
    return this.downloadFinishedMedia(recording);
  }

  /**
   * Download the server-produced finished media for a recording.
   */
  async downloadFinishedMedia(recording: Recording): Promise<void> {
    const isVideo = recording.recording_type === 'video';
    const finishedUrl = isVideo ? recording.final_video_url : recording.mixed_recording_url;

    if (!finishedUrl) {
      throw new Error(PROCESSING_MESSAGE);
    }

    const accessible = await this.storageService.verifyFileAccess(finishedUrl);
    if (!accessible) {
      throw new Error(PROCESSING_MESSAGE);
    }

    const response = await this.storageService.fetchWithRetry(finishedUrl);
    const blob = await response.blob();

    const extension = isVideo
      ? finishedUrl.includes('.mp4') ? 'mp4' : 'webm'
      : 'mp3';

    this.downloadBlob(blob, `${recording.title || 'recording'}.${extension}`);
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
